/**
 * Ayarlar — eski `procurement.php` "Finans Parametreleri" bloğunun ve KODA GÖMÜLÜ
 * sabitlerin karşılığı.
 *
 * Eski sistemde bu değerlerin bir kısmı `erp_parameters` tablosunda, bir kısmı
 * doğrudan koddaydı:
 *   · desi böleni 3000        → products_single.php:943, getpackaging.php:20, …
 *   · parite eşiği 3 / 3,5 / 4 → products_single.php:70, 214, 216 (üstelik kategori
 *     adı `strpos` ile metin araması yapılarak bulunuyordu; kategori adı
 *     değişince kural sessizce bozuluyordu)
 *   · koli kategorisi 2, dışlanan kategori 1
 * Hepsi artık `settings/finance` dokümanındadır ve buradan yönetilir.
 *
 * Yetki: YALNIZCA `boss`. Rules `settings/{id}` yazma yolunu `isBoss()` ile
 * kilitler; `settings/finance` okuması ayrıca `costView` ister.
 */

import { useEffect, useState } from "react";
import { doc, serverTimestamp, setDoc } from "firebase/firestore";
import { PageHeader, Spinner, useToast } from "../components";
import { useDoc, useCallableAction } from "../hooks";
import { db, auth } from "../lib/firebase";
import { useCan } from "../lib/auth";
import { n } from "../lib/format";
import { NoAccess } from "../components";
import { DecimalInput, ErrorNote, Field } from "../components";
import { formatDecimalInput, parseDecimal } from "../lib/decimal";
import { DeliveryForm } from "./DeliverySettings";
import { KlaviyoForm } from "./KlaviyoSettings";

/** `settings/finance`. Para dışı oranlar yüzde (20 = %20) olarak tutulur. */
export interface FinanceSettings {
  /** KDV oranı, yüzde. */
  kdv: number;
  /** Ödeme/taksit komisyonu, yüzde. */
  komisyon: number;
  /** Shopify komisyonu, yüzde. */
  shopifyKomisyon: number;
  /**
   * Trendyol NET komisyonu, yüzde (iş sahibi: %16,1). Finans ve Panel kârında
   * yalnızca Trendyol cirosuna uygulanır; ürün kartındaki kâr oranını etkilemez.
   */
  trendyolKomisyon?: number;
  /** Desi başına kargo ücreti, TL. (Oran değil, birim fiyat.) */
  kargodesifiyat: number;
  /** Desi böleni. Eskiden koda gömülü 3000'di. */
  desiDivisor: number;
  /** Ürün kategorisi → minimum kabul edilen parite (satış / maliyet). */
  parityThresholds: Record<string, number>;
  /** Kategoride tanım yoksa kullanılacak eşik. */
  defaultParityThreshold: number;
  /**
   * ★ HAVALE/EFT İNDİRİMİ, yüzde.
   *
   * TESPİT: bu alan `functions/src/core/settings.ts` tarafından OKUNUYOR ve
   * `shopify/handlers/orders.ts` her siparişin `totals.bankTransferDiscount`
   * tutarını bununla hesaplıyor — ama onu YAZAN HİÇBİR EKRAN YOKTU. Canlıda
   * değer 10 ve elle (konsoldan) konmuş. Yani "Ayarlar" ekranında görünmeyen,
   * kullanıcının asla değiştiremediği ama parayı etkileyen bir ayardı.
   */
  bankTransferDiscountPercent: number;
  /**
   * KOLİSİ OLMAYAN ÜRÜNDE KARGO TAHMİNİ — satış fiyatının yüzdesi.
   *
   * Canlı ölçüm: kolisi OLAN 259 üründe gerçek oran ağırlıklı %2,81, ortanca
   * %2,70; 201'i %2–4 bandında. Ölçüsü olmayan 257 üründe kargo hiç
   * hesaplanmıyor ve kâr olduğundan yüksek görünüyordu.
   */
  cargoEstimatePercent: number;
}

/** Eski koddaki sabitlerin birebir karşılığı — doküman hiç yoksa gösterilir. */
const DEFAULTS: FinanceSettings = {
  kdv: 10,
  komisyon: 8,
  shopifyKomisyon: 1,
  trendyolKomisyon: 16.1,
  kargodesifiyat: 16,
  desiDivisor: 3000,
  parityThresholds: { "Oturma Grubu": 3.5, Karyola: 3 },
  defaultParityThreshold: 4,
  bankTransferDiscountPercent: 10,
  cargoEstimatePercent: 5,
};

const INPUT =
  "w-full rounded border border-slate-300 px-2 py-1.5 text-sm focus:border-sky-500 focus:outline-none focus:ring-1 focus:ring-sky-500";

export default function Settings() {
  const can = useCan();

  if (!can.isBoss) {
    return (
      <PageHeader title="Ayarlar">
        <NoAccess what="KDV, komisyon, desi ve parite ayarları" />
      </PageHeader>
    );
  }

  return (
    <PageHeader title="Ayarlar">
      {/* Şirket bilgisi en üstte: iç siparişin (çekim/numune/iç kullanım) alıcı
          bilgisi buradan okunur, eksikse o ekran uyarı gösterir. */}
      <CompanyForm />
      <FinanceForm />
      {/* Teslim süreleri ayrı dokümanda (`settings/delivery`): maliyet değil,
          operasyonel bir vaat. Ürün kartı "Ayarlardaki varsayılan" derken tam
          olarak burayı işaret ediyor — daha önce bu ekran yoktu. */}
      <DeliveryForm />
      {/* Üretim aşaması ilerledikçe müşteriye giden mailler. Aşama → mesaj
          eşlemesi burada durur ki pazarlama metni değiştirmek için dağıtım
          beklenmesin. */}
      <KlaviyoForm />
      <MaintenancePanel />
    </PageHeader>
  );
}

/* ══════════════════════════════════════════════════════════════════════════
 * ŞİRKET BİLGİLERİ — `settings/company`
 * ═══════════════════════════════════════════════════════════════════════ */

/**
 * Demontha'nın kendi kimlik/adres bilgisi.
 *
 * ── NİÇİN VAR ──────────────────────────────────────────────────────────────
 * Manuel siparişte sebep "Fotoğraf çekimi" / "Numune" / "İç kullanım" ise alıcı
 * Demontha'nın KENDİSİDİR. Ofisin her seferinde kendi şirketinin adını ve
 * adresini elle yazması hem angarya hem veri kirliliğiydi: "Demontha",
 * "demontha", "DEMONTHA A.Ş." üç ayrı müşteri gibi görünür, arama ve raporlar
 * bölünür. Bilgi TEK dokümanda durur ve sunucu (`orders/manual.ts`) siparişi
 * ondan doldurur.
 *
 * ── NİÇİN KODA GÖMÜLMEDİ ───────────────────────────────────────────────────
 * Şirket adresi/telefonu değiştiği gün yeni bir dağıtım gerekirdi. Ayrıca
 * `settings/finance` içine de konmadı: o doküman yalnızca `costView` olana
 * açıktır (Rules §12) ve şirket adresi maliyet sırrı değildir — iç sipariş açan
 * herkesin görmesi gerekir. Gizlilik sınırı yanlış yere düşmesin diye ayrı
 * doküman, tıpkı `settings/delivery` gibi.
 */
interface CompanySettingsDoc {
  name?: string;
  email?: string;
  phone?: string;
  address1?: string;
  city?: string;
  province?: string;
  zip?: string;
  country?: string;
  taxOffice?: string;
  taxNumber?: string;
}

/**
 * ⚠ Yalnızca `name` ve `country` doludur; adres/vergi alanları BİLEREK boş.
 * Uydurulmuş bir adres "doğru sanılan yanlış veri"dir ve bir gün kargo
 * etiketine basılır. Sunucudaki `COMPANY_DEFAULTS` ile aynı değerler.
 */
const COMPANY_DEFAULTS: Required<CompanySettingsDoc> = {
  name: "Demontha",
  email: "",
  phone: "",
  address1: "",
  city: "",
  province: "",
  zip: "",
  country: "Türkiye",
  taxOffice: "",
  taxNumber: "",
};

function CompanyForm() {
  const toast = useToast();
  const { data, loading, error } = useDoc<CompanySettingsDoc>("settings/company");

  const [form, setForm] = useState<Required<CompanySettingsDoc>>(COMPANY_DEFAULTS);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    setForm({ ...COMPANY_DEFAULTS, ...(data ?? {}) });
  }, [data]);

  const set = (k: keyof CompanySettingsDoc) => (v: string) =>
    setForm((prev) => ({ ...prev, [k]: v }));

  async function save() {
    setErr(null);

    // Ad ZORUNLU: iç siparişin müşteri adı buradan geliyor ve boş ad sunucudaki
    // `customerName.min(2)` doğrulamasına takılır — hata sipariş ekranında,
    // sebebi ise burada olurdu. Kapıyı doğru yerde kapatıyoruz.
    if (form.name.trim().length < 2) {
      setErr("Şirket adı zorunludur — iç siparişlerde alıcı adı olarak kullanılır.");
      return;
    }

    const uid = auth.currentUser?.uid;
    if (!uid) {
      setErr("Oturum bulunamadı. Lütfen yeniden giriş yapın.");
      return;
    }

    setBusy(true);
    try {
      // Doküman ilk kez yazılıyorsa oluşturma damgaları da gerekir (Rules
      // createStamp() bunları zorunlu tutar). `merge` ile ikisi de karşılanır.
      await setDoc(
        doc(db, "settings", "company"),
        {
          name: form.name.trim(),
          email: form.email.trim(),
          phone: form.phone.trim(),
          address1: form.address1.trim(),
          city: form.city.trim(),
          province: form.province.trim(),
          zip: form.zip.trim(),
          country: form.country.trim() || "Türkiye",
          taxOffice: form.taxOffice.trim(),
          taxNumber: form.taxNumber.trim(),
          ...(data ? {} : { createdAt: serverTimestamp(), createdBy: uid, deleted: false }),
          updatedAt: serverTimestamp(),
          updatedBy: uid,
        },
        { merge: true },
      );
      toast.success("Şirket bilgileri kaydedildi.");
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Kayıt sırasında bilinmeyen bir hata oluştu.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="mb-8">
      <h2 className="mb-1 text-base font-semibold text-slate-800">Şirket Bilgileri</h2>
      <p className="mb-3 max-w-2xl text-xs text-slate-500">
        Manuel siparişte sebep <strong>fotoğraf çekimi</strong>, <strong>numune</strong> ya
        da <strong>iç kullanım</strong> seçilirse alıcı bilgileri buradan otomatik
        doldurulur — ofis kendi şirket bilgisini her seferinde elle yazmaz.
      </p>

      <ErrorNote error={error} />
      {loading ? <Spinner /> : null}

      {!loading && !data ? (
        <p className="mb-4 rounded border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900">
          Şirket bilgileri henüz kaydedilmemiş. Aşağıdaki alanları doldurup kaydedin;
          iç siparişler bu bilgilerle açılır.
        </p>
      ) : null}

      <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
        <div className="md:col-span-2">
          <Field label="Şirket Adı" hint="İç siparişlerde müşteri adı olarak yazılır.">
            <input className={INPUT} value={form.name} onChange={(e) => set("name")(e.target.value)} />
          </Field>
        </div>
        <Field label="Telefon">
          <input className={INPUT} value={form.phone} onChange={(e) => set("phone")(e.target.value)} />
        </Field>
        <Field label="E-posta">
          <input className={INPUT} value={form.email} onChange={(e) => set("email")(e.target.value)} />
        </Field>
        <div className="md:col-span-2">
          <Field label="Adres">
            <input
              className={INPUT}
              value={form.address1}
              onChange={(e) => set("address1")(e.target.value)}
            />
          </Field>
        </div>
        <Field label="İlçe">
          <input className={INPUT} value={form.city} onChange={(e) => set("city")(e.target.value)} />
        </Field>
        <Field label="İl">
          <input
            className={INPUT}
            value={form.province}
            onChange={(e) => set("province")(e.target.value)}
          />
        </Field>
        <Field label="Posta Kodu">
          <input className={INPUT} value={form.zip} onChange={(e) => set("zip")(e.target.value)} />
        </Field>
        <Field label="Vergi Dairesi">
          <input
            className={INPUT}
            value={form.taxOffice}
            onChange={(e) => set("taxOffice")(e.target.value)}
          />
        </Field>
        <Field label="Vergi / TC No">
          <input
            className={INPUT}
            value={form.taxNumber}
            onChange={(e) => set("taxNumber")(e.target.value)}
          />
        </Field>
        <Field label="Ülke">
          <input
            className={INPUT}
            value={form.country}
            onChange={(e) => set("country")(e.target.value)}
          />
        </Field>
      </div>

      <ErrorNote error={err} />

      <button
        type="button"
        onClick={() => void save()}
        disabled={busy}
        className="mt-3 rounded-lg bg-gray-900 px-5 py-2 text-sm font-medium text-white hover:bg-gray-800 disabled:opacity-50"
      >
        {busy ? "Kaydediliyor…" : "Şirket Bilgilerini Kaydet"}
      </button>
    </section>
  );
}

/**
 * BAKIM İŞLEMLERİ.
 *
 * ★ `recalculateAllCosts` ucu aylardır vardı ve HİÇBİR EKRANDAN çağrılamıyordu —
 * yazılmış ama bağlanmamış bir uç. Gerektiği an (satış fiyatları toplu
 * değiştiğinde, bileşen fiyatları güncellendiğinde) tek çare geceyi beklemekti.
 *
 * Maliyet tetikleyicileri reçeteyi ve bileşeni izler; ÜRÜN FİYATINI izleyen
 * tetikleyici yoktur. Fiyat dışarıdan toplu değişince kâr oranları eski
 * kalır — bu düğme o boşluğu kapatır.
 */
function MaintenancePanel() {
  const toast = useToast();
  const can = useCan();
  /**
   * ★ ALAN ADLARI SUNUCUDAN BİREBİR. `costing/service.ts:RecalcSummary`
   * `count / underpriced / incomplete / failed` döndürüyor. İlk sürüm burada
   * `updated` okuyordu: alan yok → `undefined` → ekranda "0 ürünün maliyeti
   * yeniden hesaplandı" yazıyordu, oysa 516 ürün hesaplanmıştı. Sessizce
   * yanlış rakam; bu projenin en sık hatası.
   */
  const run = useCallableAction<
    Record<string, never>,
    { count?: number; underpriced?: number; incomplete?: number; failed?: number }
  >("recalculateAllCosts");

  // Maliyet ve kâr yalnızca iş sahibine açıktır; düğme de öyle.
  if (!can.seeFinance) return null;

  const onRun = async () => {
    const out = await run.run({});
    if (!out) {
      toast.error(run.error ?? "Maliyetler yeniden hesaplanamadı.");
      return;
    }
    toast.success(
      `${out.count ?? 0} ürünün maliyeti yeniden hesaplandı.`,
      [
        out.underpriced ? `${out.underpriced} ürün parite eşiğinin altında` : "",
        out.incomplete ? `${out.incomplete} üründe maliyet eksik veriyle hesaplandı` : "",
        out.failed ? `${out.failed} üründe hata oluştu` : "",
      ]
        .filter(Boolean)
        .join(" · ") || undefined,
    );
  };

  return (
    <section className="mb-8">
      <h2 className="text-base font-semibold text-slate-800">Bakım</h2>
      <p className="mt-1 max-w-2xl text-sm text-slate-500">
        Satış fiyatları ya da bileşen fiyatları toplu değiştiyse maliyet ve kâr
        oranlarını hemen tazeler. Gecelik tarama bunu zaten yapar; bu düğme
        beklemeden çalıştırmak içindir.
      </p>
      <button
        type="button"
        onClick={() => void onRun()}
        disabled={run.pending}
        className="mt-3 rounded border border-slate-300 px-3 py-2 text-sm hover:bg-slate-100 disabled:opacity-50"
      >
        {run.pending ? "Hesaplanıyor…" : "Tüm maliyetleri yeniden hesapla"}
      </button>
    </section>
  );
}

function FinanceForm() {
  const toast = useToast();
  const { data, loading, error } = useDoc<FinanceSettings>("settings/finance");

  const [kdv, setKdv] = useState("");
  const [komisyon, setKomisyon] = useState("");
  const [shopifyKomisyon, setShopifyKomisyon] = useState("");
  const [trendyolKomisyon, setTrendyolKomisyon] = useState("");
  const [kargo, setKargo] = useState("");
  const [divisor, setDivisor] = useState("");
  const [defaultParity, setDefaultParity] = useState("");
  const [bankDiscount, setBankDiscount] = useState("");
  const [cargoEstimate, setCargoEstimate] = useState("");
  const [thresholds, setThresholds] = useState<Array<{ key: string; value: string }>>([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    const s = data ?? DEFAULTS;
    setKdv(formatDecimalInput(s.kdv));
    setKomisyon(formatDecimalInput(s.komisyon));
    setShopifyKomisyon(formatDecimalInput(s.shopifyKomisyon));
    setTrendyolKomisyon(formatDecimalInput(s.trendyolKomisyon ?? DEFAULTS.trendyolKomisyon));
    setKargo(formatDecimalInput(s.kargodesifiyat));
    setDivisor(formatDecimalInput(s.desiDivisor));
    setDefaultParity(formatDecimalInput(s.defaultParityThreshold));
    setBankDiscount(
      formatDecimalInput(s.bankTransferDiscountPercent ?? DEFAULTS.bankTransferDiscountPercent),
    );
    setCargoEstimate(
      formatDecimalInput(s.cargoEstimatePercent ?? DEFAULTS.cargoEstimatePercent),
    );
    setThresholds(
      Object.entries(s.parityThresholds ?? {}).map(([key, value]) => ({
        key,
        value: formatDecimalInput(value),
      })),
    );
  }, [data]);

  async function save() {
    setErr(null);

    const parsed = {
      kdv: parseDecimal(kdv),
      komisyon: parseDecimal(komisyon),
      shopifyKomisyon: parseDecimal(shopifyKomisyon),
      trendyolKomisyon: parseDecimal(trendyolKomisyon),
      kargodesifiyat: parseDecimal(kargo),
      desiDivisor: parseDecimal(divisor),
      defaultParityThreshold: parseDecimal(defaultParity),
      bankTransferDiscountPercent: parseDecimal(bankDiscount),
      cargoEstimatePercent: parseDecimal(cargoEstimate),
    };

    for (const [field, label, lo, hi] of [
      ["kdv", "KDV oranı", 0, 100],
      ["komisyon", "Komisyon oranı", 0, 100],
      ["shopifyKomisyon", "Shopify komisyonu", 0, 100],
      ["trendyolKomisyon", "Trendyol komisyonu", 0, 100],
      ["kargodesifiyat", "Desi başı kargo ücreti", 0, 100000],
      ["desiDivisor", "Desi böleni", 1, 100000],
      ["defaultParityThreshold", "Varsayılan parite eşiği", 0, 1000],
      ["bankTransferDiscountPercent", "Havale/EFT indirimi", 0, 100],
      ["cargoEstimatePercent", "Kargo tahmin oranı", 0, 100],
    ] as Array<[keyof typeof parsed, string, number, number]>) {
      const v = parsed[field];
      if (v === null) {
        setErr(`${label} okunamadı. Ondalık için virgül kullanın (örnek: 3,5).`);
        return;
      }
      if (v < lo || v > hi) {
        setErr(`${label} ${n(lo)} ile ${n(hi)} arasında olmalıdır.`);
        return;
      }
    }

    const parityThresholds: Record<string, number> = {};
    for (const row of thresholds) {
      const key = row.key.trim();
      if (!key) continue;
      const v = parseDecimal(row.value);
      if (v === null || v <= 0) {
        setErr(`"${key}" kategorisinin parite eşiği sıfırdan büyük bir sayı olmalıdır.`);
        return;
      }
      if (parityThresholds[key] !== undefined) {
        setErr(`"${key}" kategorisi iki kez tanımlanmış.`);
        return;
      }
      parityThresholds[key] = v;
    }

    const uid = auth.currentUser?.uid;
    if (!uid) {
      setErr("Oturum bulunamadı. Lütfen yeniden giriş yapın.");
      return;
    }

    setBusy(true);
    try {
      // Doküman ilk kez yazılıyorsa oluşturma damgaları da gerekir (Rules
      // createStamp() bunları zorunlu tutar). `merge` ile ikisi de karşılanır.
      await setDoc(
        doc(db, "settings", "finance"),
        {
          kdv: parsed.kdv,
          komisyon: parsed.komisyon,
          shopifyKomisyon: parsed.shopifyKomisyon,
          trendyolKomisyon: parsed.trendyolKomisyon,
          kargodesifiyat: parsed.kargodesifiyat,
          desiDivisor: parsed.desiDivisor,
          defaultParityThreshold: parsed.defaultParityThreshold,
          bankTransferDiscountPercent: parsed.bankTransferDiscountPercent,
          cargoEstimatePercent: parsed.cargoEstimatePercent,
          parityThresholds,
          ...(data
            ? {}
            : { createdAt: serverTimestamp(), createdBy: uid, deleted: false }),
          updatedAt: serverTimestamp(),
          updatedBy: uid,
        },
        { merge: true },
      );
      toast.success(
        "Ayarlar kaydedildi. Tüm ürünlerin maliyeti sunucuda yeniden hesaplanacak.",
      );
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Kayıt sırasında bilinmeyen bir hata oluştu.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <ErrorNote error={error} />
      {loading ? <Spinner /> : null}

      {!loading && !data ? (
        <p className="mb-4 rounded border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900">
          Finans ayarları henüz oluşturulmamış. Aşağıdaki değerler eski sistemdeki varsayılanlardır;
          kontrol edip kaydedin.
        </p>
      ) : null}

      <section className="mb-8">
        <h3 className="mb-1 text-base font-semibold text-slate-800">Finans Parametreleri</h3>
        <p className="mb-3 text-xs text-slate-500">
          Bu değerler değişince <strong>tüm ürünlerin</strong> maliyeti ve kâr oranı sunucuda
          yeniden hesaplanır.
        </p>

        <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
          <Field label="KDV Oranı (%)">
            <DecimalInput value={kdv} onChange={setKdv} />
          </Field>
          <Field label="Komisyon (%)" hint="Ödeme / taksit komisyonu">
            <DecimalInput value={komisyon} onChange={setKomisyon} />
          </Field>
          <Field label="Shopify Komisyonu (%)">
            <DecimalInput value={shopifyKomisyon} onChange={setShopifyKomisyon} />
          </Field>
          <Field
            label="Trendyol Komisyonu (%)"
            hint="Net kesinti. Yalnızca Trendyol cirosuna uygulanır; banka ve Shopify komisyonu Trendyol'a uygulanmaz."
          >
            <DecimalInput value={trendyolKomisyon} onChange={setTrendyolKomisyon} />
          </Field>
          <Field label="Desi Başı Kargo Ücreti (TL)">
            <DecimalInput value={kargo} onChange={setKargo} />
          </Field>
          <Field
            label="Desi Böleni"
            hint="Eskiden koda gömülü 3000'di. Kargo anlaşması değişirse burada güncellenir."
          >
            <DecimalInput value={divisor} onChange={setDivisor} />
          </Field>
          <Field label="Varsayılan Parite Eşiği" hint="Kategoride tanım yoksa kullanılır.">
            <DecimalInput value={defaultParity} onChange={setDefaultParity} />
          </Field>
          {/*
            ★ YENİ ALAN. Sunucu bu oranı okuyup her havale/EFT siparişinin
            indirimini hesaplıyordu, ama ekranda hiç görünmüyordu: kullanıcı
            parayı etkileyen bir ayarı asla değiştiremiyordu.

            ⚠ Değişiklik ANINDA yansımaz: `costing/triggers.ts` içindeki
            `FINANCE_FIELDS` listesi bu alanı içermiyor, bu yüzden 60 saniyelik
            sunucu önbelleği tazelenmez ve maliyet yeniden hesaplanmaz. Ayrıca
            yalnızca YENİ senkronlanan siparişleri etkiler. Düzeltme diff'i
            raporda verildi (o dosya bu görevin yazma sınırı dışında).
          */}
          <Field
            label="Havale/EFT İndirimi (%)"
            hint="Yeni gelen havale ödemeli siparişlere uygulanır. Geçmiş siparişleri değiştirmez."
          >
            <DecimalInput value={bankDiscount} onChange={setBankDiscount} />
          </Field>

          {/* ★ TAHMİN, ÖLÇÜM DEĞİLDİR. Bu oranla hesaplanan kargo maliyete
              girer ama ürün "maliyeti tam ölçülmüş" sayılmaz; ürün kartında ve
              Excel'in Uyarı sütununda oranla hesaplandığı yazar. 0 = kapalı. */}
          <Field
            label="Kolisiz Ürün Kargo Tahmini (%)"
            hint="Koli ölçüsü girilmemiş üründe kargo, satış fiyatının bu yüzdesi sayılır. Ölçülü ürünlerdeki gerçek oran: ortalama %2,8. 0 yazılırsa tahmin kapanır."
          >
            <DecimalInput value={cargoEstimate} onChange={setCargoEstimate} />
          </Field>
        </div>
      </section>

      {/* ── Kategori bazlı parite eşikleri ───────────────────────────────── */}
      <section className="mb-8">
        <h3 className="mb-1 text-base font-semibold text-slate-800">Parite Eşikleri</h3>
        <p className="mb-3 text-xs text-slate-500">
          Parite = satış fiyatı / toplam maliyet. Eşiğin altındaki ürün kartında{" "}
          <strong>"Fiyat ucuz!"</strong> uyarısı çıkar. Eski sistemde bu uyarı hesaplanıyor ama
          hiçbir yere yazılmıyordu. Bilinen değerler: Oturma Grubu 3,5 · Karyola 3 · diğer 4.
        </p>

        <div className="overflow-x-auto rounded border border-slate-200">
          <table className="min-w-full text-sm">
            <thead className="bg-slate-100 text-left text-xs uppercase tracking-wide text-slate-600">
              <tr>
                <th className="px-3 py-2">Ürün Kategorisi</th>
                <th className="px-3 py-2 w-40">Eşik</th>
                <th className="px-3 py-2 w-24" />
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {thresholds.map((row, i) => (
                <tr key={`row-${i}`}>
                  <td className="px-3 py-2">
                    <input
                      className={INPUT}
                      value={row.key}
                      placeholder="Örn. Oturma Grubu"
                      aria-label="Kategori adı"
                      onChange={(e) =>
                        setThresholds((prev) =>
                          prev.map((r, j) => (j === i ? { ...r, key: e.target.value } : r)),
                        )
                      }
                    />
                  </td>
                  <td className="px-3 py-2">
                    <DecimalInput
                      value={row.value}
                      aria-label="Parite eşiği"
                      onChange={(t) =>
                        setThresholds((prev) =>
                          prev.map((r, j) => (j === i ? { ...r, value: t } : r)),
                        )
                      }
                    />
                  </td>
                  <td className="px-3 py-2 text-right">
                    <button
                      type="button"
                      className="rounded border border-red-300 px-2 py-1 text-xs text-red-700 hover:bg-red-50"
                      onClick={() => setThresholds((prev) => prev.filter((_, j) => j !== i))}
                    >
                      Sil
                    </button>
                  </td>
                </tr>
              ))}
              {thresholds.length === 0 ? (
                <tr>
                  <td colSpan={3} className="px-3 py-3 text-sm text-slate-500">
                    Kategori bazlı eşik tanımlı değil; tüm ürünlerde varsayılan eşik kullanılır.
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>

        <button
          type="button"
          className="mt-2 rounded border border-slate-300 px-3 py-1.5 text-sm hover:bg-slate-100"
          onClick={() => setThresholds((prev) => [...prev, { key: "", value: "" }])}
        >
          Kategori Ekle
        </button>
      </section>

      <ErrorNote error={err} />

      <div className="sticky bottom-0 -mx-4 border-t border-slate-200 bg-white/95 px-4 py-3 backdrop-blur">
        <button
          type="button"
          onClick={save}
          disabled={busy}
          className="rounded-lg bg-gray-900 px-5 py-2 text-sm font-medium text-white hover:bg-gray-800 disabled:opacity-50"
        >
          {busy ? "Kaydediliyor…" : "Ayarları Kaydet"}
        </button>
      </div>
    </>
  );
}
